import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { useHeaderSearch } from '../../../hooks/useHeaderSearch';

function SearchIcon() {
  return (
    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
    </svg>
  );
}

function HeaderSearch() {
  const containerRef = useRef(null);
  const {
    query,
    setQuery,
    open,
    setOpen,
    loading,
    error,
    suggestions,
    clearSearch,
  } = useHeaderSearch();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setOpen]);

  const showPanel = open && query.trim().length > 0;

  return (
    <div ref={containerRef} className="relative hidden w-[220px] shrink-0 md:block lg:w-[260px] xl:w-[310px]">
      <label htmlFor="site-search" className="sr-only">
        Search
      </label>
      <div className="relative flex w-full items-center">
        <span className="pointer-events-none absolute left-1 rounded-full bg-black p-2">
          <SearchIcon />
        </span>
        <input
          id="site-search"
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search Anything..."
          className="w-full rounded-full border-2 border-[#1B1B1B] bg-white py-2 pl-12 pr-4 text-sm text-[#1B1B1B] placeholder:text-[#999999] focus:outline-none lg:py-2.5"
          autoComplete="off"
        />
      </div>

      {showPanel && (
        <div className="absolute left-0 right-0 top-full z-[60] mt-2 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_12px_40px_rgba(0,0,0,0.14)]">
          {loading && !suggestions.length ? (
            <p className="px-4 py-3 text-sm text-[#666666]">Searching...</p>
          ) : error ? (
            <p className="px-4 py-3 text-sm text-red-600">{error}</p>
          ) : suggestions.length ? (
            <ul className="max-h-[320px] overflow-y-auto py-2">
              {suggestions.map((course) => (
                <li key={course.id}>
                  <Link
                    to={ROUTES.COURSE_DETAIL(course)}
                    onClick={clearSearch}
                    className="flex items-start gap-3 px-4 py-2.5 transition hover:bg-[#F7F2FF]"
                  >
                    {course.thumbnailUrl ? (
                      <img
                        src={course.thumbnailUrl}
                        alt=""
                        className="mt-0.5 h-10 w-10 shrink-0 rounded-lg object-cover"
                      />
                    ) : (
                      <span
                        className={`mt-0.5 h-10 w-10 shrink-0 rounded-lg bg-gradient-to-br ${course.gradient}`}
                        aria-hidden
                      />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-[#1B1B1B]">
                        {course.title}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-[#666666]">
                        {course.subject}
                        {course.classLabel ? ` · ${course.classLabel}` : ''}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-3 text-sm text-[#666666]">No courses found</p>
          )}
        </div>
      )}
    </div>
  );
}

export default HeaderSearch;
